/* eslint-disable import/prefer-default-export */
import { useCallback, useMemo } from 'react';
import { useApiClient, useFetch, useFetchInfinite } from '@marageti/z4-lib';
import { FilterOp, SearchRequest } from '@marageti/z4-sdk/lib/entity';
import { ChecklistItem, TripRequestStatus } from '@marageti/z4-sdk/lib/travel';
import {
  AssignedConversationsResponse,
  ConversationType,
  ConversationWithTripDetails,
} from '@marageti/z4-sdk/lib/travel/Conversation';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { SWRConfiguration, SWRResponse } from 'swr';
import type {
  SWRInfiniteConfiguration,
  SWRInfiniteKeyLoader,
  SWRInfiniteResponse,
} from 'swr/infinite';
import { useRemindersCount } from '../../../hooks/use-reminders-search';

export type InboxName = 'Traveler Inbox' | 'Partner Inbox';
export type TripRequestMatchStateName = 'In Progress' | 'Match Declined' | 'Postponed' | 'Following';
export type RemindersSectionName = 'Reminders';
export type ListName = InboxName | TripRequestMatchStateName | RemindersSectionName;
export type TravelerTag = 'All' | 'VIP' | 'Repeat';
export type SortDirection = 'asc' | 'desc';

export type BucketCounts = {
  [key in ListName]?: number | undefined;
};

export type TripRequestBucketCounts = {
  [key in TripRequestMatchStateName]: number | undefined;
};

export const getSearch = (name: ListName, userId: string, limit = 30): SearchRequest => {
  switch (name) {
    case 'In Progress':
      return ({
        filterBy: [
          { field: 'assignedTo', value: [userId], op: FilterOp.FilterOpTerm },
          { field: 'status', value: [TripRequestStatus.PendingStatus, TripRequestStatus.VerifiedStatus], op: FilterOp.FilterOpTerm },
          { field: 'checklist', value: [ChecklistItem.NeedsScreening, ChecklistItem.NeedsMatching], op: FilterOp.FilterOpTerm },
        ],
        sortBy: [{ field: 'createdAt', order: 'desc' }],
        pagination: { limit, page: 1 },
      });
    case 'Match Declined':
      return ({
        filterBy: [
          { field: 'assignedTo', value: [userId], op: FilterOp.FilterOpTerm },
          { field: 'status', value: [TripRequestStatus.MatchedStatus], op: FilterOp.FilterOpTerm },
          { field: 'checklist', value: [ChecklistItem.NeedsMatching], op: FilterOp.FilterOpTerm },
        ],
        sortBy: [{ field: 'createdAt', order: 'desc' }],
        pagination: { limit, page: 1 },
      });
    case 'Postponed':
      return ({
        filterBy: [
          { field: 'assignedTo', value: [userId], op: FilterOp.FilterOpTerm },
          { field: 'status', value: [TripRequestStatus.PostponedStatus], op: FilterOp.FilterOpTerm },
        ],
        sortBy: [{ field: 'createdAt', order: 'desc' }],
        pagination: { limit, page: 1 },
      });
    case 'Following':
      return ({
        filterBy: [
          { field: 'followers', value: [userId], op: FilterOp.FilterOpTerm },
        ],
        sortBy: [{ field: 'createdAt', order: 'desc' }],
        pagination: { limit, page: 1 },
      });
    default:
      return ({
        filterBy: [
          { field: 'assignedTo', value: [userId], op: FilterOp.FilterOpTerm },
        ],
        pagination: { limit: 0, page: 1 },
      });
  }
};

export const getConversationParams = (
  inboxType: InboxName,
  travelerTag: TravelerTag = 'All',
  sortDirection: SortDirection = 'asc',
  hasNewOnly: boolean = true,
) => {
  const params: Record<string, string> = {
    sort: sortDirection,
  };

  if (inboxType === 'Traveler Inbox') {
    params.conversationType = ConversationType.TravelerToZicasso;
  } else {
    params.conversationType = ConversationType.ZicassoToPartner;
  }

  if (hasNewOnly) {
    params.hasNewOnly = 'true';
  }

  if (travelerTag !== 'All') {
    params.travelerTag = travelerTag;
  }

  return params;
};

/** Matches travel-service `assignedConversationsMaxLimit` (max page size for GET .../assigned). */
export const ASSIGNED_CONVERSATIONS_PAGE_LIMIT = 30;

export type AssignedConversationsInfiniteKey = readonly [
  'assigned-conversations',
  InboxName,
  TravelerTag,
  SortDirection,
  boolean,
  number,
];

const inboxListSWRConfig: SWRInfiniteConfiguration<AssignedConversationsResponse> = {
  revalidateOnFocus: true,
  refreshInterval: 1000 * 60 * 3,
  revalidateFirstPage: true,
};

/**
 * Sidebar total for an inbox bucket (minimal payload via limit=1; uses API totalHits).
 */
export const useInboxSidebarTotal = (inboxType: InboxName) => {
  const apiClient = useApiClient();

  async function fetchInboxSidebarTotal(inboxKey: InboxName): Promise<number> {
    const params = {
      ...getConversationParams(inboxKey, 'All', 'asc', false),
      limit: '1',
      page: '1',
    };
    const result = await apiClient.conversationsClient.getAssignedConversations({ params });
    return result.totalHits;
  }

  return useFetch<number>(
    fetchInboxSidebarTotal,
    [inboxType],
    true,
    { revalidateOnFocus: true, refreshInterval: 1000 * 60 * 3, revalidateOnMount: true } satisfies SWRConfiguration,
  );
};

/** Extends the underlying `useSWRInfinite` response with three derived fields the UI consumes */
export type UseAssignedConversationsInfiniteResult =
  SWRInfiniteResponse<AssignedConversationsResponse, ApiError> & {
    flattenedHits: ConversationWithTripDetails[];
    hasMore: boolean;
    loadMore: () => void;
  };

/**
 * Paginated assigned-conversations fetch backed by `useSWRInfinite`. Returns the full
 * accumulated list of hits across all loaded pages along with `hasMore` and `loadMore`
 * for infinite scroll.
 *
 * Pass `enabled = false` to skip fetching entirely (e.g. when not on the inbox section);
 * the hook will return cached/empty state without issuing any network calls.
 */
export const useAssignedConversationsInfinite = (
  inboxType: InboxName,
  travelerTag: TravelerTag,
  sortDirection: SortDirection,
  hasNewOnly: boolean,
  enabled: boolean,
): UseAssignedConversationsInfiniteResult => {
  const apiClient = useApiClient();

  const getKey: SWRInfiniteKeyLoader<AssignedConversationsResponse, AssignedConversationsInfiniteKey | null> = useCallback(
    (pageIndex, previousPageData) => {
      if (!enabled) return null;
      if (previousPageData) {
        const { totalHits, hits, pagination } = previousPageData;
        const loaded = (pagination.page - 1) * pagination.limit + hits.length;
        if (loaded >= totalHits) return null;
      }
      return [
        'assigned-conversations',
        inboxType,
        travelerTag,
        sortDirection,
        hasNewOnly,
        pageIndex + 1,
      ] as const;
    },
    [enabled, inboxType, travelerTag, sortDirection, hasNewOnly],
  );

  const fetcher = useCallback(
    async (key: AssignedConversationsInfiniteKey) => {
      const [, type, tag, sort, newOnly, page] = key;
      const params = {
        ...getConversationParams(type, tag, sort, newOnly),
        limit: String(ASSIGNED_CONVERSATIONS_PAGE_LIMIT),
        page: String(page),
      };
      return apiClient.conversationsClient.getAssignedConversations({ params });
    },
    [apiClient],
  );

  const response = useFetchInfinite<AssignedConversationsResponse, ApiError>(
    getKey,
    fetcher,
    inboxListSWRConfig,
  );
  const {
    data: pages,
    size,
    setSize,
    isValidating,
  } = response;

  const flattenedHits = useMemo<ConversationWithTripDetails[]>(
    () => pages?.flatMap((page) => page.hits) ?? [],
    [pages],
  );

  const lastPage = pages?.[pages.length - 1];
  const hasMore = lastPage
    ? (lastPage.pagination.page - 1) * lastPage.pagination.limit + lastPage.hits.length < lastPage.totalHits
    : false;

  const loadMore = useCallback(() => {
    if (!isValidating) setSize(size + 1);
  }, [isValidating, size, setSize]);

  return {
    ...response,
    flattenedHits,
    hasMore,
    loadMore,
  };
};

export const useTripRequestBucketCounts = (userId: string) => {
  const apiClient = useApiClient();

  const fetcher = async (): Promise<TripRequestBucketCounts> => {
    const tripRequestResults = await Promise.all(
      [
        apiClient.tripRequestsClient.postTripRequestSearches(getSearch('In Progress', userId, 0)),
        apiClient.tripRequestsClient.postTripRequestSearches(getSearch('Match Declined', userId, 0)),
        apiClient.tripRequestsClient.postTripRequestSearches(getSearch('Postponed', userId, 0)),
        apiClient.tripRequestsClient.postTripRequestSearches(getSearch('Following', userId, 0)),
      ],
    );

    return {
      'In Progress': tripRequestResults[0]?.totalHits,
      'Match Declined': tripRequestResults[1]?.totalHits,
      Postponed: tripRequestResults[2]?.totalHits,
      Following: tripRequestResults[3]?.totalHits,
    };
  };

  return useFetch<TripRequestBucketCounts>(
    fetcher,
    [],
    true,
    { revalidateOnFocus: true, refreshInterval: 1000 * 60 * 3 },
  );
};

export const useBucketCounts = (userId: string) : SWRResponse<BucketCounts> => {
  const travelerInboxTotal = useInboxSidebarTotal('Traveler Inbox');
  const partnerInboxTotal = useInboxSidebarTotal('Partner Inbox');
  const tripRequestBucketCounts = useTripRequestBucketCounts(userId);
  const remindersCount = useRemindersCount({ assignedTo: userId, statusFilter: 'pending' });

  return {
    ...tripRequestBucketCounts,
    data: {
      'Traveler Inbox': travelerInboxTotal.data,
      'Partner Inbox': partnerInboxTotal.data,
      Reminders: remindersCount.data,
      ...tripRequestBucketCounts.data,
    } as BucketCounts,
    mutate: async () => {
      const [
        tripRequestsRevalidated,
        travelerInboxRevalidated,
        partnerInboxRevalidated,
        remindersRevalidated,
      ] = await Promise.all([
        tripRequestBucketCounts.mutate(),
        travelerInboxTotal.mutate(),
        partnerInboxTotal.mutate(),
        remindersCount.mutate(),
      ]);
      return {
        'Traveler Inbox': travelerInboxRevalidated,
        'Partner Inbox': partnerInboxRevalidated,
        Reminders: remindersRevalidated,
        ...tripRequestsRevalidated,
      } as BucketCounts;
    },
  };
};

export const inboxNameToType = (name: ListName): ConversationType.ZicassoToPartner | ConversationType.TravelerToZicasso => {
  switch (name) {
    case 'Traveler Inbox':
      return ConversationType.TravelerToZicasso;
    case 'Partner Inbox':
      return ConversationType.ZicassoToPartner;
    default:
      return ConversationType.TravelerToZicasso;
  }
};
